import { ActionIcon, Badge, Button, Group, Paper, Stack, Text, TextInput, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconFileDescription, IconSearch, IconX } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { Table, TableCellValue } from '@/components/Table'
import { useGlobalState } from '@/hooks/useGlobalState'
import { AnalyzingBadge } from '@/screens/ContentModuleFormScreen/AnalyzingBadge'
import type { GeneralInfoContent } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { formatContentType } from '@/utils/contentTypes'
import type { TableColumnDef } from '@/utils/table'

import { GeneralInfoEditModal } from './GeneralInfoEditModal'
import { GeneralInfoUploadModal } from './GeneralInfoUploadModal'

type GIDisplayStatus = 'analyzing' | 'processing' | 'review_needed' | 'published' | 'incomplete'

function deriveDisplayStatus(item: GeneralInfoContent): GIDisplayStatus {
  if (item.inProcess) return 'analyzing'
  if (item.status === 'incomplete') return 'incomplete'
  if (item.status === 'processing') return 'processing'

  if (item.status === 'published') {
    const source = item.resources.find(r => r.isSourceFile)
    if (source) {
      const hasConfirmedTitle = !!source.title?.trim()
      const hasConfirmedDesc = !!source.description?.trim()

      if (!hasConfirmedTitle || !hasConfirmedDesc) {
        return 'review_needed'
      }
    }
    return 'published'
  }

  return 'processing'
}

function getDisplayTitle(item: GeneralInfoContent): string {
  const source = item.resources.find(r => r.isSourceFile)
  if (source?.title?.trim()) return source.title
  if (source?.titleAi?.trim()) return source.titleAi
  return item.fileName || 'Untitled'
}

interface GeneralInfoPanelProps {
  showTutorialText?: boolean
  registerUnsavedChangesChecker: (tabKey: 'generalInformation', checker: () => boolean, onDiscard: () => void) => void
}

export function GeneralInfoPanel({ showTutorialText = false, registerUnsavedChangesChecker }: GeneralInfoPanelProps) {
  const { api } = useGlobalState()
  const [uploadOpened, uploadHandlers] = useDisclosure(false)
  const [editOpened, editHandlers] = useDisclosure(false)
  const [editContentId, setEditContentId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    registerUnsavedChangesChecker(
      'generalInformation',
      () => false,
      () => {},
    )
  }, [registerUnsavedChangesChecker])

  const listQuery = useQuery({
    queryKey: ['general-info', 'list'],
    queryFn: () => api.listGeneralInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const hasProcessingItems = useMemo(
    () => listQuery.data?.some(item => item.inProcess || item.status === 'processing') ?? false,
    [listQuery.data],
  )

  const refetchList = listQuery.refetch

  useEffect(() => {
    if (!hasProcessingItems) return
    const interval = setInterval(() => {
      refetchList()
    }, 5000)
    return () => clearInterval(interval)
  }, [hasProcessingItems, refetchList])

  const deleteMutation = useMutation({
    mutationFn: (contentId: string) => api.deleteGeneralInfo(contentId),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'list'] })
      notifications.show({
        title: 'Content Deleted',
        message: 'The file was successfully deleted.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => {
      notifications.show({
        title: 'Delete Failed',
        message: 'Could not delete the content. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  function handleDelete(item: GeneralInfoContent) {
    modals.openConfirmModal({
      title: 'Delete content?',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this content? This action is destructive and cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete Content', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => deleteMutation.mutate(item.contentId),
    })
  }

  function handleEdit(item: GeneralInfoContent) {
    const status = deriveDisplayStatus(item)
    if (status === 'analyzing') return
    setEditContentId(item.contentId)
    editHandlers.open()
  }

  const columns: TableColumnDef<GeneralInfoContent>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        accessorFn: row => getDisplayTitle(row),
        cell: props => (
          <Text size="sm" fw={500}>
            {props.getValue() ? <TableCellValue cell={props.cell} /> : 'Untitled'}
          </Text>
        ),
        size: 280,
      },
      {
        id: 'contentType',
        header: 'Type',
        accessorFn: row => {
          const source = row.resources.find(r => r.isSourceFile)
          return formatContentType(source?.type)
        },
        enableGlobalFilter: false,
        size: 100,
        meta: { grow: false },
      },
      {
        id: 'status',
        header: 'Status',
        enableGlobalFilter: false,
        size: 158,
        meta: { grow: false },
        cell: ({ row }) => {
          const status = deriveDisplayStatus(row.original)

          switch (status) {
            case 'analyzing':
              return <AnalyzingBadge />
            case 'processing':
              return (
                <Badge color="yellow" variant="filled">
                  Processing
                </Badge>
              )
            case 'review_needed':
              return (
                <Badge color="orange" variant="filled">
                  Review Needed
                </Badge>
              )
            case 'published':
              return (
                <Badge color="green" variant="filled">
                  Published
                </Badge>
              )
            case 'incomplete':
              return (
                <Badge color="red" variant="filled">
                  Incomplete
                </Badge>
              )
          }
        },
      },
    ],
    [],
  )

  const data = listQuery.data ?? []

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Title order={4} mb="md">
          General Information Content
        </Title>

        {showTutorialText && (
          <Text size="sm" c="dimmed" mb="lg">
            Add general information content that will be available to all Sidekicks, such as your organization&apos;s
            history or mission statement.
          </Text>
        )}

        {data.length > 0 && (
          <Group justify="flex-end" mb="xs">
            <TextInput
              size="xs"
              placeholder="Search by title..."
              leftSection={<IconSearch size={14} stroke={1.5} />}
              rightSection={
                searchQuery ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search">
                    <IconX size={12} aria-hidden="true" />
                  </ActionIcon>
                ) : null
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.currentTarget.value)}
              w={260}
            />
          </Group>
        )}

        <Table
          data={data}
          columns={columns}
          rowActionsInclude={['edit', 'delete']}
          enableTopToolbar={false}
          enableGlobalFilter={true}
          globalFilter={searchQuery}
          onGlobalFilterChange={setSearchQuery}
          enableRowVirtualization={true}
          enableStickyHeader={true}
          containerProps={{ style: { maxHeight: '60vh' }, className: 'general-info-scroll-container' }}
          rowVirtualizerOverscan={5}
          onRowActionClick={(action, row) => {
            if (action === 'delete') {
              handleDelete(row.original)
            } else if (action === 'edit') {
              handleEdit(row.original)
            }
          }}
          rowActionsTooltipLabels={row => ({
            edit:
              deriveDisplayStatus(row.original) === 'analyzing'
                ? 'Content is still being analyzed'
                : 'Edit & Configure',
            delete: 'Delete',
          })}
          rowActionsButtonProps={row => ({
            edit: { disabled: deriveDisplayStatus(row.original) === 'analyzing' },
          })}
          EmptyStateProps={{
            title: 'No Files Added',
            description: 'Add general information content that will be available to all Sidekicks.',
            Icon: IconFileDescription,
          }}
          actionsColumnSize={98}
        />

        <Group justify="center" mt="xl">
          <Button loading={listQuery.isFetching && !listQuery.data} onClick={uploadHandlers.open}>
            Add File
          </Button>
        </Group>
      </Paper>

      <GeneralInfoUploadModal opened={uploadOpened} onClose={uploadHandlers.close} />

      {editContentId && (
        <GeneralInfoEditModal opened={editOpened} onClose={editHandlers.close} contentId={editContentId} />
      )}
    </Stack>
  )
}
